/*!
 * @Author: leo
 * @Date: 2019-12-27 18:12:00
 * @Last Modified by: leo
 * @Last Modified time: 2019-12-27 18:12:00
 * @Introduction: 工具类
 */

import { isEqual, isPlainObject, set } from 'lodash'
import { SizeOption, ImageViewOption } from '@/types/utils-types'
import { IMG_HOST, HOST } from '@/config/env'

/**
 * 内部代理地址补全 API 域名。
 * 后端归一输出的相对路径（/v1/img?key=...、/v1/annex/download）必须拼上 API 域名，
 * 否则请求打到前端自己的域名，会被 SPA fallback 成 index.html
 * （表现为"点击下载回到主页"、图片破图）。
 * 外链 / 七牛完整 URL 已含域名，原样返回。
 */
export const withApiHost = (path: string) => {
    if (!path) return path
    if (/^https?:\/\//i.test(path)) return path
    return HOST.replace(/\/+$/, '') + path
}

export const isValidUsername = (str: string) => /^[a-zA-Z\u4E00-\u9FA5][a-zA-Z0-9\u4E00-\u9FA5_-]{0,15}$/.test(str)

export const isExternal = (path: string) => /^(https?:|mailto:|tel:)/.test(path)

export const isArray = (arg: any) => {
    if (typeof Array.isArray === 'undefined') {
        return Object.prototype.toString.call(arg) === '[object Array]'
    }
    return Array.isArray(arg)
}

export const isValidURL = (url: string) => {
    const reg = /^https?:\/\/([a-zA-Z0-9]+\.)+[a-zA-Z0-9]+/
    return reg.test(url)
}

/**
 * 最多一位小数
 * @param {String} content 待校验的字符串
 * @return {boolean} 是否正确
 */
export const isValidScore = (str: any) => {
    const reg = /^\d+(?:\.\d{1,1})?$/
    return reg.test(str)
}

/**
 * 校验邮箱
 * @param {String} content 待校验的字符串
 * @return {boolean} 是否正确
 */
export const isValidEmail = (str: any) => {
    const reg = /^[a-zA-Z0-9_-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/
    return reg.test(str)
}

/**
 * 全局替换换行符为 <br/>
 * @param {String} content 待转字符串
 * @return {String} 目标字符串
 */
export const replaceContent = (content: string) => {
    if (!content) return ''
    return content.replace(/\n|\r\n/g, '<br/>')
}

/**
 * Parse the time to string
 * @param {(Object|string|number)} time
 * @param {string} cFormat
 * @returns {string | null}
 */
export const parseTime = (time?: object | string | number, cFormat?: string): string | null => {
    if (time === undefined) {
        return null
    }
    const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
    let date: Date
    if (typeof time === 'object') {
        date = time as Date
    } else {
        if (typeof time === 'string' && /^[0-9]+$/.test(time)) {
            time = parseInt(time)
        }
        if (typeof time === 'number' && time.toString().length === 10) {
            time = time * 1000
        }
        date = new Date(time)
    }
    const formatObj: { [key: string]: number } = {
        y: date.getFullYear(),
        m: date.getMonth() + 1,
        d: date.getDate(),
        h: date.getHours(),
        i: date.getMinutes(),
        s: date.getSeconds(),
        a: date.getDay()
    }
    const timeStr = format.replace(/{([ymdhisa])+}/g, (result, key) => {
        const value = formatObj[key]
        // Note: getDay() returns 0 on Sunday
        if (key === 'a') {
            return ['日', '一', '二', '三', '四', '五', '六'][value]
        }
        return value.toString().padStart(2, '0')
    })
    return timeStr
}

/**
 * 计算互动比例（千分比）
 * @date 2021-01-11
 * @param {number} interactCount    互动数
 * @param {number} readCount        阅读数
 * @param {boolean} isFormat        是否格式化
 * @returns {string}
 */
export const calcIntReadRatio = (interactCount = 0, readCount = 0, isFormat: boolean) => {
    if (interactCount === 0 || readCount === 0) {
        return isFormat ? '-' : '0‰'
    }
    const intReadRatio = ((interactCount / readCount) * 1000).toFixed(2)
    if (+intReadRatio > 1000) {
        return '1000‰+'
    }
    return intReadRatio + '‰'
}

/**
 * 旧数据和新数据对比，是否发生改变
 * @param {any} origin
 * @param {any} now
 * @returns {boolean}
 */
export const isChange = (origin: any, now: any) => !isEqual(origin, now)

/**
 * 是否是准确类型并且含有数据
 * @date 2021-07-22
 * @param {any} type:'array'|'object'
 * @param {any} data:any
 * @returns {any}
 */
export const hasData = (type: 'array' | 'object', data: any) => {
    switch (type) {
        case 'array':
            return isArray(data) && data.length > 0
        case 'object':
            return isPlainObject(data) && Object.keys(data > 0)
        default:
            return false
    }
}

/**
 * 命名空间自定义 Array
 */

export const iArray = {
    /**
     * 删除数组中的一项
     * 会改变原有数组
     * @date 2021-07-22
     * @param {any} item
     * @param {any[]} list
     * @returns {any}
     */
    remove<T>(item: T, list: T[]) {
        list.splice(list.indexOf(item), 1)
    },
    /**
     * 获取元素在数组中的位置
     * false 为不存在于数组中
     * @date 2021-07-23
     * @param {any} item
     * @param {any[]} list
     * @returns {any}
     */
    getIndex<T>(item: T, list: T[]) {
        const index = list.indexOf(item)
        return index !== -1 ? index : false
    }
}

/**
 * 命名空间自定义 File
 */
export const iFile = {
    /**
     * 将base64转换为blob
     * @date 2021-07-28
     * @param {string} dataurl
     * @returns {any}
     */
    dataURLtoBlob(dataUrl: string) {
        let arr = dataUrl.split(','),
            // @ts-ignore
            mime = arr[0].match(/:(.*?);/)[1],
            bstr = atob(arr[1]),
            n = bstr.length,
            u8arr = new Uint8Array(n)
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
        }
        return new Blob([u8arr], { type: mime })
    },
    /**
     * 将blob转换为file
     * @date 2021-07-28
     * @param {any} theBlob
     * @param {string} fileName
     * @returns {any}
     */
    blobToFile(theBlob: Blob, fileName: string) {
        set(theBlob, 'lastModifiedDate', new Date())
        set(theBlob, 'name', fileName)
        return theBlob
    },
    /**
     * base64 直接转 file 对象
     * @date 2021-07-28
     * @param {any} dataUrl:string
     * @param {any} fileName:string
     * @returns {any}
     */
    dataUrlToFile(dataUrl: string, fileName: string) {
        let tempBlob = this.dataURLtoBlob(dataUrl)
        return this.blobToFile(tempBlob, fileName)
    },
    /**
     * 将base64转换为blob 
     * @date 2021-07-28
     * @param {string} baseData  base64字符串
     * @returns {any}
     */
    base64DataToBlob(baseData: any) {
        let bstr = atob(baseData)
        let n = bstr.length
        let u8arr = new Uint8Array(n)
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n)
        }
        return new Blob([u8arr], { type: 'application/pdf;charset-UTF-8;application/vnd.ms-excel' })
    },
    /**
     * base64文件 => 下载
     * @date 2021-07-28
     * @param {string} baseData  base64字符串
     * @returns {any}
     */
    downloadBase64File(baseData: any, fileName: any) {
        let URL = this.base64DataToBlob(baseData)
        let reader = new FileReader()
        reader.readAsDataURL(URL)
        reader.onload = function(e: any) {
            // 转换完成，创建一个a标签用于下载
            const a = document.createElement('a')
            a.download = fileName // 这里写你的文件名
            a.href = e.target.result
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
            a.remove()
        }
    }
}

/**
 * 生成字节大小, 单位字节
 * @date 2021-04-13
 * @returns {any}
 */
export const createSize = ({ size, type }: SizeOption) => {
    switch (type) {
        case 'MB':
            return Math.pow(1024, 2) * size
        case 'KB':
            return Math.pow(1024, 1) * size
        case 'BYTE':
            return size
        default:
            return 0
    }
}

/**
 * 增加图片域名
 * @date 2021-10-29
 * @param {string} path
 * @returns {any}
 */
export const addImgHost = (path: string) => {
    if (!path) return path
    const hasHost = path.includes(IMG_HOST)
    if (hasHost) {
        return path
    } else {
        return IMG_HOST + path
    }
}

/**
 * 计算 pageIndex
 * @date 2021-10-29
 * @param {number} dataLength   数据列表长度
 * @param {number} curPageIndex 当前页数
 * @returns {any}
 */
export const calcPageIndex = (dataLength: number, curPageIndex: number) => {
    // 1. 数据列表长度 === 1
    //  当前页数 === 1, 返回 1, 否则返回 - 1
    if (dataLength === 1) {
        return curPageIndex === 1 ? 1 : curPageIndex - 1
    } else if (dataLength > 1) {
        return curPageIndex
    }
    return curPageIndex
}

/**
 * 对图片链接做 iamgeView2 处理
 * @date 2021-11-25
 * @param {string} link
 * @param {any} option
 * @description https://developer.qiniu.com/dora/1279/basic-processing-images-imageview2
 * @returns {any}
 */
export const createImageViewTo = (link: string, option?: ImageViewOption) => {
    const defaultOption: ImageViewOption = {
        mode: 2,
        width: 0,
        height: 0,
        quality: 75
    }
    option = { ...defaultOption, ...option }
    const { mode, width, height, quality } = option
    return `${link}?imageView2/${mode}/w/${width}/h/${height}/q/${quality}`
}

/**
 * 延迟函数返回，返回一个新函数
 * @date 2021-10-19
 * @param {function} fn      执行的函数
 * @param {number} delay   延迟时间
 * @returns {function}
 * @description 输入一个函数，默认情况下，执行时间在 delay 时间或更之后返回
 */
export const linear = (fn: Function, delay = 500) => {
    // @ts-ignore
    return (...args) =>
        new Promise(async (resolve, reject) => {
            const start = +new Date()
            let result: any = null
            const isAsyncFn = (fn: Function) => Object.prototype.toString.call(fn).includes('AsyncFunction')
            try {
                if (isAsyncFn(fn)) {
                    result = await fn(args)
                } else {
                    result = fn(args)
                }
                const interval = +new Date() - start
                // 1000 代表一秒
                if (interval < delay) {
                    setTimeout(() => {
                        resolve(result)
                    }, delay - interval)
                }
            } catch (error) {
                reject(error)
            }
        })
}

/**
 * 点击下载文件
 * @date 2021-08-18
 * @param {string | Blob} blob
 * @param {string} fileName
 * @returns {any}
 */
export const downloadFile = (blob: Blob, fileName: string, isUrl = false) => {
    let downloadUrl: any = ''
    if (isUrl) {
        downloadUrl = blob
    } else {
        downloadUrl = window.URL.createObjectURL(blob)
    }
    const link = document.createElement('a')
    link.download = fileName
    link.href = downloadUrl
    link.click()
}

/**
 * 是否命中文件后缀
 * @date 2021-12-21
 * @param {any} filename 文件名
 * @param {any} suffixs  文件后缀
 * @returns {any}
 */
export const isHitSuffix = (filename: string, suffixs: string[]) => {
    let isValid = false
    suffixs.some((item) => {
        const suffix = item.toLocaleLowerCase()
        if (filename.endsWith(suffix)) {
            isValid = true
            return true
        }
    })
    return isValid
}

export class MediaStateStorage {
    private static KEY = 'media_list_state'
    private static EXPIRE_TIME = 4 * 24 * 60 * 60 * 1000

    static saveState(state: {
        searchQuery?: string
        classStatus?: number | string
        mpTypeId?: string
        region?: string
        regionNumber?: string[]
        selectedTagIds?: number[]
        pageIndex?: number
    }) {
        const data = { ...state, timestamp: Date.now() }
        localStorage.setItem(this.KEY, JSON.stringify(data))
    }

    static getState() {
        try {
            const data = localStorage.getItem(this.KEY)
            if (!data) return {}
            const parsed = JSON.parse(data)
            if (Date.now() - parsed.timestamp > this.EXPIRE_TIME) {
                this.clearState()
                return {}
            }
            delete parsed.timestamp
            return parsed
        } catch (error) {
            return {}
        }
    }

    static clearState() {
        localStorage.removeItem(this.KEY)
    }
}

export class UserStateStorage {
    private static KEY = 'user_list_state'
    private static EXPIRE_TIME = 4 * 24 * 60 * 60 * 1000

    static saveState(state: {
        name?: string
        department?: string
        role?: number | undefined
        mobile?: string
        email?: string
        pageIndex?: number
    }) {
        const data = { ...state, timestamp: Date.now() }
        localStorage.setItem(this.KEY, JSON.stringify(data))
    }

    static getState() {
        try {
            const data = localStorage.getItem(this.KEY)
            if (!data) return {}
            const parsed = JSON.parse(data)
            if (Date.now() - parsed.timestamp > this.EXPIRE_TIME) {
                this.clearState()
                return {}
            }
            delete parsed.timestamp
            return parsed
        } catch (error) {
            return {}
        }
    }

    static clearState() {
        localStorage.removeItem(this.KEY)
    }
}
